import {
  keepPreviousData,
  type QueryClient,
  mutationOptions,
  queryOptions,
} from "@tanstack/react-query";
import {
  getOperatorsHttpOrigin,
  type OperatorsSkillDetail,
  type OperatorsSkillStatus,
  type OperatorsSessionSkillsResponse,
  type OperatorsSkillsResponse,
} from "../operators";
import { readOperatorsAuthErrorMessage, throwOperatorsAuthExpired } from "../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "./operatorsReactQuery";

export const operatorsSkillQueryKeys = {
  all: [...operatorsQueryKeys.all, "skills"] as const,
  detail: (id: string) => [...operatorsSkillQueryKeys.all, "detail", id] as const,
  list: (includeArchived: boolean) =>
    [...operatorsSkillQueryKeys.all, "list", includeArchived] as const,
  session: (sessionId: string) => [...operatorsSkillQueryKeys.all, "session", sessionId] as const,
};

async function skillsResponse(input: {
  apiPath?: string;
  fallbackError: string;
  init?: RequestInit;
  path?: string;
}): Promise<Response> {
  const response = await fetch(
    `${getOperatorsHttpOrigin()}${input.apiPath ?? "/api/v1/skills"}${input.path ?? ""}`,
    {
      credentials: "include",
      ...input.init,
    },
  );
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (response.status === 403) {
    throw new OperatorsForbiddenError(
      await readOperatorsAuthErrorMessage(response).catch(() => "Skill access denied."),
    );
  }
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => input.fallbackError));
  }
  return response;
}

const skillsFetch = async <T>(input: Parameters<typeof skillsResponse>[0]): Promise<T> =>
  (await (await skillsResponse(input)).json()) as T;

const jsonInit = (method: string, body: unknown): RequestInit => ({
  body: JSON.stringify(body),
  headers: { "Content-Type": "application/json" },
  method,
});

const zipInit = (zip: File, fields: Record<string, string> = {}): RequestInit => {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    form.append(key, value);
  }
  form.append("file", zip);
  return { body: form, method: "POST" };
};

export function operatorsSkillsQueryOptions(includeArchived = false) {
  return queryOptions({
    queryFn: async () =>
      skillsFetch<OperatorsSkillsResponse>({
        fallbackError: "Could not load skills.",
        path: includeArchived ? "?includeArchived=true" : "",
      }),
    placeholderData: keepPreviousData,
    queryKey: operatorsSkillQueryKeys.list(includeArchived),
    staleTime: 5_000,
  });
}

export function operatorsSessionSkillsQueryOptions(input: { enabled: boolean; sessionId: string }) {
  return queryOptions({
    enabled: input.enabled,
    queryFn: async () =>
      skillsFetch<OperatorsSessionSkillsResponse>({
        apiPath: `/api/v1/sessions/${encodeURIComponent(input.sessionId)}/skills`,
        fallbackError: "Could not load session skills.",
      }),
    queryKey: operatorsSkillQueryKeys.session(input.sessionId),
    staleTime: 5_000,
  });
}

export function operatorsSkillQueryOptions(id: string) {
  return queryOptions({
    queryFn: async () =>
      skillsFetch<OperatorsSkillDetail>({
        fallbackError: "Could not load skill.",
        path: `/${encodeURIComponent(id)}`,
      }),
    queryKey: operatorsSkillQueryKeys.detail(id),
  });
}

export type OperatorsSkillMutation =
  | { files: Record<string, string>; kind: "create" }
  | { kind: "import"; zip: File }
  | { edits: Record<string, string | null>; id: string; kind: "save" }
  | { id: string; kind: "replace"; zip: File }
  | { id: string; kind: "update"; locked?: boolean; status?: OperatorsSkillStatus }
  | { id: string; kind: "duplicate"; name: string };

const requestFor = (
  variables: OperatorsSkillMutation,
): { fallbackError: string; init: RequestInit; path: string } => {
  switch (variables.kind) {
    case "create":
      return {
        fallbackError: "Could not create skill.",
        init: jsonInit("POST", { files: variables.files }),
        path: "",
      };
    case "import":
      return {
        fallbackError: "Could not import skill.",
        init: zipInit(variables.zip),
        path: "/import",
      };
    case "save":
      return {
        fallbackError: "Could not save skill.",
        init: jsonInit("POST", { edits: variables.edits }),
        path: `/${variables.id}/versions`,
      };
    case "replace":
      return {
        fallbackError: "Could not replace skill files.",
        init: zipInit(variables.zip),
        path: `/${variables.id}/versions/import`,
      };
    case "update":
      return {
        fallbackError: "Could not update skill.",
        init: jsonInit("PATCH", { locked: variables.locked, status: variables.status }),
        path: `/${variables.id}`,
      };
    case "duplicate":
      return {
        fallbackError: "Could not duplicate skill.",
        init: jsonInit("POST", { name: variables.name }),
        path: `/${variables.id}/duplicate`,
      };
  }
};

/** Every skill write returns the fresh detail, which seeds its cache entry. */
export function operatorsSkillMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: async (variables: OperatorsSkillMutation) =>
      await skillsFetch<OperatorsSkillDetail>(requestFor(variables)),
    onSuccess: async (skill) => {
      queryClient.setQueryData(operatorsSkillQueryKeys.detail(skill.id), skill);
      await queryClient.invalidateQueries({ queryKey: operatorsSkillQueryKeys.all });
    },
  });
}

// The API origin differs from the UI, so Content-Disposition is not readable; name it here.
export async function downloadOperatorsSkillExport(skill: {
  id: string;
  name: string;
}): Promise<void> {
  const response = await skillsResponse({
    fallbackError: "Could not export skill.",
    path: `/${encodeURIComponent(skill.id)}/export`,
  });
  const fileName = `${skill.name}.zip`;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
